import { useCallback, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import useApi from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { apiErrorCode, apiErrorMessage } from '../../lib/event'
import CheckInOutModal from '../../pages/events/VolunteerList/CheckInOutModal'
import LoginModal from '../Login/LoginModal'

const KNOWN_ERROR_CODES = [
  'invalid_check_in_token',
  'outside_check_in_window',
  'shift_self_check_in_unsupported',
  'waitlisted',
  'already_checked_in',
  'not_checked_in',
  'already_checked_out',
]

// Orchestrates the QR self check-in/out flow. Volunteers land on the
// event page with ?check_in_token=... after scanning the organizer's QR
// code; without a token this renders nothing.
export default function SelfCheckInFlow({ event, setRefresh }) {
  const { t } = useTranslation()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { volunteerSelfCheckIn, volunteerSelfCheckOut } = useApi()
  const { currentUser } = useCurrentUser()

  const token = searchParams.get('check_in_token')

  const [busy, setBusy] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [notSignedUp, setNotSignedUp] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [notice, setNotice] = useState(null)

  const loggedIn = !!currentUser && !!Object.keys(currentUser).length
  const isVolunteer = !!event?.current_user_volunteers
  const volunteer = event?.current_user_volunteer
  const isShiftEvent = event?.tasks?.length > 0

  // Signing up mid-flow refetches the event and flips
  // current_user_volunteers; resume the flow to offer check-in.
  useEffect(() => {
    if (isVolunteer) {
      setNotSignedUp(false)
      setDismissed(false)
    }
  }, [isVolunteer])

  // The token is secret: remove it from the URL once the flow ends so it
  // cannot be shared or bookmarked.
  const clearToken = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete('check_in_token')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const finish = useCallback(() => {
    setNotice(null)
    setDismissed(true)
    clearToken()
  }, [clearToken])

  const confirm = async checkingIn => {
    if (busy) return
    setBusy(true)
    const call = checkingIn ? volunteerSelfCheckIn : volunteerSelfCheckOut
    let response
    try {
      response = await call({ eventId: event.id, checkInToken: token })
    } finally {
      setBusy(false)
    }
    if (response?.status === 200) {
      clearToken()
      setNotice({
        success: true,
        message: checkingIn
          ? t('selfCheckIn.success_in')
          : t('selfCheckIn.success_out'),
      })
      setRefresh(+new Date())
      return
    }
    const code = apiErrorCode(response)
    if (code === 'not_signed_up') {
      setNotSignedUp(true)
      return
    }
    setNotice({
      success: false,
      message: KNOWN_ERROR_CODES.includes(code)
        ? t(`selfCheckIn.errors.${code}`)
        : apiErrorMessage(response) || t('selfCheckIn.errors.generic'),
    })
  }

  let step = 'inactive'
  if (notice) {
    step = 'notice'
  } else if (!token || !event || dismissed) {
    step = 'inactive'
  } else if (!loggedIn) {
    step = 'login'
  } else if (notSignedUp || !isVolunteer) {
    step = isShiftEvent ? 'shift_unsupported' : 'not_signed_up'
  } else if (!volunteer?.check_in_time) {
    step = 'confirm_in'
  } else if (!volunteer?.check_out_time) {
    step = 'confirm_out'
  } else {
    step = 'already_done'
  }

  if (step === 'inactive') {
    return null
  }

  if (step === 'confirm_in' || step === 'confirm_out') {
    return (
      <CheckInOutModal
        show
        selfService
        isCheckIn={step === 'confirm_in'}
        eventTitle={event.title}
        disabled={busy}
        onHide={finish}
        onConfirm={() => confirm(step === 'confirm_in')}
      />
    )
  }

  if (step === 'login') {
    return (
      <>
        <Modal show={!showLogin} onHide={finish} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('selfCheckIn.login_title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('selfCheckIn.login_details', { title: event.title })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={finish}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={() => setShowLogin(true)}>
              {t('selfCheckIn.login_button')}
            </Button>
          </Modal.Footer>
        </Modal>
        <LoginModal
          show={showLogin}
          returnUrl={location.pathname + location.search}
          onClose={(reload = true) => {
            localStorage.removeItem('return-url')
            setShowLogin(false)
            if (reload) {
              window.location.reload()
            }
          }}
        />
      </>
    )
  }

  // Terminal notices: not_signed_up keeps the token so the flow resumes
  // after sign-up; everything else clears it.
  const notices = {
    notice: {
      title: t('selfCheckIn.notice_title'),
      message: notice?.message,
      onHide: finish,
    },
    not_signed_up: {
      title: t('selfCheckIn.not_signed_up_title'),
      message: t('selfCheckIn.not_signed_up_details', { title: event.title }),
      onHide: () => setDismissed(true),
    },
    shift_unsupported: {
      title: t('selfCheckIn.notice_title'),
      message: t('selfCheckIn.shift_unsupported'),
      onHide: finish,
    },
    already_done: {
      title: t('selfCheckIn.notice_title'),
      message: t('selfCheckIn.already_done'),
      onHide: finish,
    },
  }
  const { title, message, onHide } = notices[step]

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{message}</Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          {t('buttons.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
